import React, { useState } from 'react';
import { Button } from '@material-tailwind/react';
import { MapPinIcon, PlusIcon, XIcon } from 'lucide-react';
import LocationPicker from './LocationPicker';
import { useGetBranches } from '../../../custom-hooks/use-branches';
import { useGetBuildings } from '../../../custom-hooks/use-buildings';
import { GetFloors } from '../../../services/backend';
import { NotifyError } from '../../../utils/functions';

// Picks one or more locations for a work order — a burst pipe can hit four floors at
// once, and adding them one at a time was the complaint that prompted this.
//
// The draft row reuses the existing single LocationPicker rather than reimplementing
// the cascade, so Maintenance, Inventory and Support keep the exact same branch →
// building → floor behaviour. Selected locations live in a list below it.
const EMPTY_DRAFT = { branch: '', building: '', floor: '', space: '' };

const MultiLocationPicker = ({ locations = [], onChange, disabled = false }) => {
    const [draft, setDraft] = useState(EMPTY_DRAFT);
    const [bulkLoading, setBulkLoading] = useState(false);

    const { data: branches = [] } = useGetBranches();
    const { data: allBuildings = [] } = useGetBuildings();

    const branchName = (id) => branches.find((b) => b._id === String(id))?.branchName || '';
    const buildingName = (id) => allBuildings.find((b) => b._id === String(id))?.name || '';

    // Floor numbers aren't in any list this component already has — they arrive with the
    // location when it's added, so the label is built once at add time and carried on
    // the entry itself. Re-deriving it on every render would mean a query per building.
    const addLocations = (incoming) => {
        const existing = new Set(locations.map((l) => String(l.floor)));
        const fresh = incoming.filter((l) => l.floor && !existing.has(String(l.floor)));

        if (fresh.length === 0) {
            NotifyError(incoming.length === 1
                ? 'That floor is already in the list.'
                : 'Every floor there is already in the list.');
            return 0;
        }
        onChange([...locations, ...fresh]);
        return fresh.length;
    };

    const handleAddDraft = async () => {
        if (!draft.branch || !draft.building || !draft.floor) return;

        // The draft picker knows the floor's id but not its number, so fetch the
        // building's floors to build a readable label for the list entry.
        try {
            const floors = await GetFloors(draft.building).then((res) => res?.data?.floors || []);
            const floor = floors.find((f) => f._id === draft.floor);
            const added = addLocations([{
                branch: draft.branch,
                building: draft.building,
                floor: draft.floor,
                space: (draft.space || '').trim(),
                floorNumber: floor?.number ?? null,
            }]);
            // The branch/building stay selected so adding several floors of the same
            // building is a floor pick and a click, not a full re-cascade each time.
            if (added > 0) setDraft((prev) => ({ ...prev, floor: '', space: '' }));
        } catch (error) {
            NotifyError('Could not add that location', error);
        }
    };

    // Adds every floor of every building under a branch (or of one building). The user
    // then removes whatever isn't relevant — far less clicking than adding fifteen
    // floors by hand, which is the whole point of this control.
    const handleAddBulk = async (scope) => {
        const targetBuildings = scope === 'branch'
            ? allBuildings.filter((b) => String(b.branch?._id || b.branch) === String(draft.branch))
            : allBuildings.filter((b) => b._id === draft.building);

        if (targetBuildings.length === 0) return;

        setBulkLoading(true);
        try {
            const results = await Promise.all(
                targetBuildings.map((b) =>
                    GetFloors(b._id)
                        .then((res) => (res?.data?.floors || []).map((f) => ({
                            branch: draft.branch,
                            building: b._id,
                            floor: f._id,
                            space: '',
                            floorNumber: f.number,
                        })))
                        // One building failing shouldn't lose the floors of the others.
                        .catch(() => [])
                )
            );

            const flat = results.flat();
            if (flat.length === 0) {
                NotifyError('No floors found to add.');
                return;
            }
            addLocations(flat);
        } finally {
            setBulkLoading(false);
        }
    };

    const removeLocation = (index) => onChange(locations.filter((_, i) => i !== index));

    const canAddDraft = draft.branch && draft.building && draft.floor && !disabled;

    return (
        <div className="flex flex-col gap-3">
            <LocationPicker
                branch={draft.branch}
                building={draft.building}
                floor={draft.floor}
                space={draft.space}
                disabled={disabled}
                onChange={(updates) => setDraft((prev) => ({ ...prev, ...updates }))}
            />

            {/* Wraps rather than sitting in a fixed row — three buttons don't fit across
                a phone, and a non-wrapping row here was what pushed the modal into
                horizontal scroll. */}
            <div className="flex flex-wrap gap-2">
                <Button
                    size="sm"
                    type="button"
                    className="flex items-center gap-1.5 rounded-full bg-inv-brand px-4 shadow-none hover:bg-inv-brand-dark"
                    disabled={!canAddDraft}
                    onClick={handleAddDraft}
                >
                    <PlusIcon size={14} /> Add floor
                </Button>
                <Button
                    size="sm"
                    type="button"
                    variant="outlined"
                    className="rounded-full border-inv-cream-200 px-4 text-inv-ink"
                    disabled={!draft.building || disabled || bulkLoading}
                    onClick={() => handleAddBulk('building')}
                >
                    Add entire building
                </Button>
                <Button
                    size="sm"
                    type="button"
                    variant="outlined"
                    className="rounded-full border-inv-cream-200 px-4 text-inv-ink"
                    disabled={!draft.branch || disabled || bulkLoading}
                    onClick={() => handleAddBulk('branch')}
                >
                    {bulkLoading ? 'Adding…' : 'Add entire branch'}
                </Button>
            </div>

            {locations.length === 0 ? (
                <p className="rounded-lg border border-dashed border-inv-cream-200 px-3 py-3 text-center text-xs text-inv-muted">
                    No locations added yet. Pick a floor above, or add a whole building or branch and remove what you don't need.
                </p>
            ) : (
                <div className="flex flex-col gap-1.5">
                    <p className="text-xs font-semibold text-inv-ink">
                        {locations.length} location{locations.length === 1 ? '' : 's'} selected
                    </p>
                    {/* Capped height so adding a whole branch of twenty floors scrolls
                        inside the list instead of pushing the form's buttons off-screen. */}
                    <div className="flex max-h-52 flex-col gap-1.5 overflow-y-auto">
                        {locations.map((loc, index) => (
                            <div
                                key={`${loc.floor}-${index}`}
                                className="flex items-center gap-2 rounded-lg bg-inv-cream-100 px-3 py-2"
                            >
                                <MapPinIcon size={13} className="shrink-0 text-inv-brand" />
                                {/* min-w-0 is what actually lets the truncate below work —
                                    a flex child defaults to min-width:auto and refuses to
                                    shrink past its text, overflowing the row on mobile. */}
                                <span className="min-w-0 flex-1 truncate text-xs text-inv-ink">
                                    {[
                                        branchName(loc.branch),
                                        buildingName(loc.building),
                                        loc.floorNumber != null ? `Floor ${loc.floorNumber}` : null,
                                        loc.space || null,
                                    ].filter(Boolean).join(' / ')}
                                </span>
                                {!disabled && (
                                    <button
                                        type="button"
                                        aria-label="Remove location"
                                        className="shrink-0 rounded-full p-1 text-inv-muted transition-colors hover:bg-inv-cream-200 hover:text-inv-ink"
                                        onClick={() => removeLocation(index)}
                                    >
                                        <XIcon size={13} />
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default MultiLocationPicker;
